/**
 * Runtime configuration, read from the global that web/config.js installs before the bundle runs.
 *
 * Nothing here is baked in at build time: the anon key is gitignored and the API origin differs
 * between dev and production, while the bundle is the same in both.
 */
export interface RuntimeConfig {
  /** Origin of the Supabase API. Empty = same origin as the portal, which is how Caddy serves it. */
  apiBaseUrl: string;
  /** The public anon JWT (`ANON_KEY` in .env). */
  anonKey: string;
  /** Forces the UI language instead of the browser's. */
  defaultLocale: string | null;
  /** `false` switches the Centrifugo connection off (everything polls). Absent = on. */
  realtime?: boolean;
  /** `false` hides "Continue with Google" (the kill switch). Absent = on. */
  googleSignIn?: boolean;
  /** `true` shows "Continue with Apple", once the web Service ID exists. Absent = off. */
  appleSignIn?: boolean;
  /** The address the "access paused" screen of a suspended agency offers. Absent = a generic line. */
  supportEmail?: string | null;
  /** Origin of the public site that serves the Terms and the Privacy Policy. Absent = the production site. */
  legalBaseUrl?: string;
  /** The Photon geocoder the address search asks from the browser. Absent = komoot's public instance;
   *  `''` or `false` = the search is off (the stop forms are exactly the hand-typed ones). */
  photonUrl?: string | false;
  /** A PUBLIC Mapbox token (`pk.`) for the map tiles of the Location card. Absent = no map, text only. */
  mapboxToken?: string;
  /** The Mapbox style of the tiles, `owner/style`. Absent = `mapbox/streets-v12`. */
  mapStyle?: string;
  /** The PUBLIC Cloudflare Turnstile site key of the "Create my agency" form. Absent = self sign-up unavailable. */
  turnstileSiteKey?: string;
}

declare global {
  interface Window {
    __COREWAY_CONFIG__?: Partial<RuntimeConfig>;
  }
}

export class ConfigError extends Error {}

const PLACEHOLDER = 'replace-me-with-the-anon-key';

/**
 * Reads and validates the runtime config. Throws rather than falling back to a default: a portal
 * running against the wrong API with a silently empty key is worse than one that refuses to boot.
 */
export function loadConfig(): RuntimeConfig {
  const raw = window.__COREWAY_CONFIG__;
  if (!raw) {
    throw new ConfigError(
      'config.js did not load. Copy web/config.example.js to web/config.js and fill it in.',
    );
  }
  if (!raw.anonKey || raw.anonKey === PLACEHOLDER) {
    throw new ConfigError('config.js has no anonKey. Set it to the ANON_KEY value from .env.');
  }
  return {
    apiBaseUrl: raw.apiBaseUrl?.replace(/\/$/, '') || window.location.origin,
    anonKey: raw.anonKey,
    defaultLocale: raw.defaultLocale ?? null,
    realtime: raw.realtime !== false,
    googleSignIn: raw.googleSignIn !== false,
    appleSignIn: raw.appleSignIn === true,
    supportEmail: supportEmail(raw),
    legalBaseUrl: legalBaseUrl(raw),
  };
}

const LEGAL_SITE = 'https://corewaydispatch.com';

/**
 * The origin of the public site the Terms gate links to, trimmed and without a trailing slash. Absent,
 * blank or not an http(s) URL falls back to the production site (a typo in config.js must never render
 * a broken or scriptable link on the screen that asks for acceptance).
 */
export function legalBaseUrl(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): string {
  const value = typeof raw?.legalBaseUrl === 'string' ? raw.legalBaseUrl.trim() : '';
  if (!/^https?:\/\/[^\s/]+/i.test(value)) return LEGAL_SITE;
  return value.replace(/\/+$/, '');
}

/** The Terms and Privacy pages in the UI language: Spanish under `/es/`, anything else in English. */
export function legalLinks(
  locale: string | undefined,
  raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__,
): { terms: string; privacy: string } {
  const base = `${legalBaseUrl(raw)}${locale === 'es' ? '/es' : ''}`;
  return { terms: `${base}/terms/`, privacy: `${base}/privacy/` };
}

/** The public Support page (hours, what to include, security reports) in the UI language. */
export function supportPage(locale: string | undefined, raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): string {
  return `${legalBaseUrl(raw)}${locale === 'es' ? '/es' : ''}/support/`;
}

/**
 * The support address of the runtime config, trimmed; null when absent, blank or not an address (a
 * typo in config.js must not render a broken mail link). Read straight from the global, like the
 * OAuth flags.
 */
export function supportEmail(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): string | null {
  const value = typeof raw?.supportEmail === 'string' ? raw.supportEmail.trim() : '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : null;
}

/**
 * The OAuth ways in this portal offers: Google unless switched off, Apple only when switched on. Read
 * straight from the global, like `realtime` (the flags are not plumbed through React).
 */
export function oauthProviders(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): { google: boolean; apple: boolean } {
  return { google: raw?.googleSignIn !== false, apple: raw?.appleSignIn === true };
}

const PHOTON_DEFAULT = 'https://photon.komoot.io';

/**
 * The Photon endpoint the address search calls from the browser (portal-address-map design 3.1), without
 * a trailing slash; null = search off. `''` / `false` switch it off on purpose. A value that is not an
 * https URL (or http on localhost, for a dev instance) is off too, with a warning: a typo must never send
 * typed addresses to an unknown host.
 */
export function photonUrl(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): string | null {
  const value = raw?.photonUrl;
  if (value === undefined || value === null) return PHOTON_DEFAULT;
  if (value === false) return null;
  const text = typeof value === 'string' ? value.trim() : '';
  if (text === '') return null;
  if (!/^https:\/\/[^\s/]+\S*$/i.test(text) && !/^http:\/\/localhost(:\d+)?(\/\S*)?$/i.test(text)) {
    console.warn('photonUrl must be an https URL; address search is off');
    return null;
  }
  return text.replace(/\/+$/, '');
}

const MAP_STYLE_DEFAULT = 'mapbox/streets-v12';

/**
 * The map tiles of the Location card (design 6.1): a PUBLIC Mapbox token and a style, or null (no map).
 * A secret `sk.` token (or anything that is not a `pk.` token) is refused with a warning: config.js is
 * served to anyone, and a secret token must never ride in it.
 */
export function mapTiles(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): { token: string; style: string } | null {
  const token = typeof raw?.mapboxToken === 'string' ? raw.mapboxToken.trim() : '';
  if (token === '') return null;
  if (!/^pk\.[A-Za-z0-9._-]+$/.test(token)) {
    console.warn('mapboxToken must be a public pk. token; the map is off');
    return null;
  }
  const style = typeof raw?.mapStyle === 'string' && /^[\w-]+\/[\w-]+$/.test(raw.mapStyle.trim()) ? raw.mapStyle.trim() : MAP_STYLE_DEFAULT;
  return { token, style };
}

/**
 * self-signup: the public Turnstile site key of the "Create my agency" form, trimmed; null when absent,
 * blank or malformed (the form then says sign-up is unavailable and never submits: the server refuses any
 * agency without a captcha pass anyway).
 */
export function turnstileSiteKey(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): string | null {
  const value = typeof raw?.turnstileSiteKey === 'string' ? raw.turnstileSiteKey.trim() : '';
  return /^[\w-]+$/.test(value) ? value : null;
}

/**
 * self-signup: where the portal hands a Turnstile token to coreway-billing. Built from `apiBaseUrl` exactly
 * as the Supabase client's URL is (`loadConfig`: empty = the portal's own origin, which is how Caddy serves
 * `/billing/captcha` in production).
 */
export function captchaUrl(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): string {
  const base = (typeof raw?.apiBaseUrl === 'string' ? raw.apiBaseUrl.trim() : '').replace(/\/$/, '') || window.location.origin;
  return `${base}/billing/captcha`;
}

